// ---------------------------------------------------------------------------
// S0-162 / S0-163. SIGN IN WITH EVENT.CLINIC, on verbunk.event.clinic only.
//
// event.clinic's Clerk is loaded from its own Frontend API with the PUBLIC publishable
// key (/Users/petermarik/Documents/event-clinic-suite/ecosystem/docs/identity/CLERK-SHARED-SIGN-IN.md),
// so Clerk itself enters no bundle. The key is given at BUILD time as
// VITE_CLERK_PUBLISHABLE_KEY (Clerk's convention, and the hub's document names it): it is
// public, but a live key is credential-shaped and the secret scan rightly refuses one in
// tracked files. Without it the button is simply not offered. A person already signed in on any event.clinic host
// has a session here too (one Clerk instance, one parent domain); anyone else signs in on
// THIS page (XP-5): Clerk's embedded sign-in is mounted here under Verbunk's name and mark
// (clerkBrand.ts), never a redirect to the instance's Account Portal. Its localisation is a lazily
// loaded chunk. Signed in, Clerk returns them to this page to finish. The token is then
// exchanged at POST /v1/auth/clerk for this console's own cookie: what they may do here is
// still Verbunk's memberships, never Clerk's.
// ---------------------------------------------------------------------------

import type { Locale } from "../i18n/translate";
import { ownSignInOptions, PRODUCT_NAME } from "./clerkBrand";

export const CLERK_PUBLISHABLE_KEY = ((import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ?? "").trim();
export const CLERK_FRONTEND = "https://clerk.event.clinic";
/** The page returns here with this marker, so it finishes without a second click. */
export const RETURN_MARKER = "event_clinic";

type ClerkGlobal = {
  load: (opts: Record<string, unknown>) => Promise<void>;
  session?: { getToken: () => Promise<string | null> } | null;
  mountSignIn: (node: HTMLDivElement, opts: Record<string, unknown>) => void;
};

let loading: Promise<ClerkGlobal> | null = null;

function loadClerk(locale: Locale): Promise<ClerkGlobal> {
  loading ??= new Promise<ClerkGlobal>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${CLERK_FRONTEND}/npm/@clerk/clerk-js@5/dist/clerk.browser.js`;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.setAttribute("data-clerk-publishable-key", CLERK_PUBLISHABLE_KEY);
    script.onload = async () => {
      const clerk = (window as unknown as { Clerk?: ClerkGlobal }).Clerk;
      if (!clerk) return reject(new Error("the sign-in did not load"));
      const { enUS } = await import("@clerk/localizations/en-US");
      const translation = locale === "hu" ? (await import("@clerk/localizations/hu-HU")).huHU : null;
      clerk.load(ownSignInOptions(enUS, translation, window.location.origin)).then(() => resolve(clerk), reject);
    };
    script.onerror = () => reject(new Error("the sign-in could not be reached"));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    loading = null;
    throw err;
  });
  return loading;
}

/** The session token, or null when nobody is signed in yet. */
export async function eventClinicToken(locale: Locale): Promise<string | null> {
  const clerk = await loadClerk(locale);
  return clerk.session ? await clerk.session.getToken() : null;
}

/** Clerk's sign-in, drawn into `node` on this page; signed in, it returns to `returnTo`. */
export async function mountOwnSignIn(node: HTMLDivElement, locale: Locale, returnTo: string): Promise<void> {
  const clerk = await loadClerk(locale);
  clerk.mountSignIn(node, { routing: "hash", withSignUp: true, forceRedirectUrl: returnTo, signUpForceRedirectUrl: returnTo });
  // Clerk names the logo after the instance and offers no option for it; the alt text is ours.
  new MutationObserver(() => node.querySelectorAll("img.cl-logoImage").forEach((img) => img.setAttribute("alt", PRODUCT_NAME))).observe(
    node,
    { childList: true, subtree: true },
  );
}
